import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Sparkles, Wrench, Zap, Shield, Clock, BarChart3, Globe } from "lucide-react";

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="https://www.machinerymarketed.com/app-logo.png" 
            alt="MachineryMarketed Logo" 
            className="h-10 w-10"
          />
          <span className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </Link>
        <Link to="/" className="btn-outline text-sm">Back to Home</Link>
      </div>
    </nav>
  );
};

// Feature Card Component
const FeatureCard = ({ icon: Icon, title, description, image }) => (
  <div className="card overflow-hidden group" style={{ background: "var(--background-200)" }}>
    <div className="h-48 overflow-hidden">
      <img 
        src={image} 
        alt={title}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        loading="lazy"
      />
    </div>
    <div className="p-6">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: "var(--primary-100)" }}>
        <Icon size={24} style={{ color: "white" }} />
      </div>
      <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
        {title}
      </h3>
      <p style={{ color: "var(--text-200)" }}>{description}</p>
    </div>
  </div>
);

// Main Features Page Component
const FeaturesPage = () => {
  const features = [
    {
      icon: Wrench,
      title: "Digital Appraisals",
      description: "Capture every detail with guided photo workflows and structured data entry.",
      image: "https://www.machinerymarketed.com/app-appraisal.jpg"
    },
    {
      icon: BarChart3,
      title: "Smart Valuations",
      description: "AI-powered market analysis and instant PDF reports for your team.",
      image: "https://www.machinerymarketed.com/app-valuation.jpg"
    },
    {
      icon: Globe,
      title: "Multi-Channel Publishing",
      description: "One click to publish across marketplaces, social media, and your website.",
      image: "https://www.machinerymarketed.com/app-machine-details.jpg"
    },
    {
      icon: Zap,
      title: "Lightning Fast",
      description: "Complete appraisals in 10-15 minutes and get listings live instantly.",
      image: "https://www.machinerymarketed.com/app-create-account.jpg"
    },
    {
      icon: Clock,
      title: "Offline Capability",
      description: "Work anywhere, even without signal. Auto-sync when you're back online.",
      image: "https://www.machinerymarketed.com/excavator_ground.jpg"
    },
    {
      icon: Shield,
      title: "Secure & Backed Up",
      description: "Enterprise-grade security with automatic cloud backups of all your data.",
      image: "https://www.machinerymarketed.com/loader_yard.jpg"
    }
  ];

  return (
    <div style={{ background: "var(--background-100)", minHeight: "100vh" }}>
      <Navigation />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="relative py-20">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://www.machinerymarketed.com/hero_excavator.jpg" 
              alt="Construction machinery"
              className="w-full h-full object-cover"
              style={{ opacity: 0.2 }}
              loading="eager"
            />
            <div 
              className="absolute inset-0"
              style={{ background: "linear-gradient(to bottom, rgba(0,12,37,0.8) 0%, var(--background-100) 100%)" }}
            />
          </div>
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-6"
                style={{ background: "rgba(15, 95, 255, 0.2)", border: "1px solid rgba(15, 95, 255, 0.3)" }}
              >
                <Sparkles size={18} style={{ color: "var(--primary-300)" }} />
                <span style={{ color: "var(--text-100)" }}>Powerful Tools</span>
              </div>
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-8"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                Platform <span className="text-gradient">Features</span>
              </h1>
              <p className="text-xl max-w-3xl mx-auto" style={{ color: "var(--text-200)" }}>
                Everything you need to turn trade-ins into cash faster. 
                Built by dealership people, for dealership people.
              </p>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <FeatureCard key={index} {...feature} />
              ))}
            </div>
          </div>
        </section>

        {/* Placeholder for more content */}
        <section className="py-20" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
              More Features Coming Soon
            </h2>
            <p className="max-w-2xl mx-auto mb-8" style={{ color: "var(--text-200)" }}>
              We're constantly adding new capabilities to help you move metal faster. 
              Check back soon for detailed feature documentation and video tutorials.
            </p>
            <div className="flex justify-center gap-4">
              <Link to="/" className="btn-primary">Back to Home</Link>
              <Link to="/contact" className="btn-outline">Contact Us</Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{ background: "var(--background-200)", borderTop: "1px solid rgba(176, 176, 176, 0.1)" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://www.machinerymarketed.com/app-logo.png" 
                alt="MachineryMarketed Logo" 
                className="h-10 w-10"
              />
              <span className="font-bold text-lg" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                MACHINERYMARKETED
              </span>
            </div>

            <div className="flex items-center gap-6">
              <Link to="/" className="footer-link text-sm">Home</Link>
              <Link to="/about" className="footer-link text-sm">About</Link>
              <Link to="/privacy" className="footer-link text-sm">Privacy</Link>
              <Link to="/faq" className="footer-link text-sm">FAQ</Link>
            </div>

            <p className="text-sm" style={{ color: "var(--text-300)" }}>
              © 2026 MachineryMarketed. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FeaturesPage;
